import {
  ChevronRight as ChevronRightIcon,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SxProps,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import CustomSearch from '../../../components/General/CustomSearch';
import CustomTable from '../../../components/General/CustomTable';
import apiClient from '../../../requests/api/apiClient';
import {
  AccountSchema, FarmSchema,
  NodeSchema, SensorModelSchema,
} from '../../../requests/api/apiTypes';
import { capitalizeFirstLetter } from '../../../utils/helpers';
import { STATUS_COLORS } from '../../ClientDetail/SensorStatus/constant';

export type DeviceType = 'nodes' | 'sensors';

export interface GetNodesPerPageResponse {
  success?: boolean;
  message?: string;
  nodes?: NodeSchema[];
  totalNodes?: number;
}

export interface GetSensorModelsPerPageResponse {
  success?: boolean;
  message?: string;
  sensorModels?: SensorModelSchema[];
  totalSensorModels?: number;
}

export interface SummaryState {
  total: number | undefined;
  operative: number | undefined;
  offline: number | undefined;
  repair: number | undefined;
  disabled: number | undefined;
}

const GetNodesListUI = (adminView: boolean) => [
  { label: 'Nombre', key: 'name' },
  ...(adminView ? [{ label: 'Cuenta', key: 'account' }] : []),
  ...(adminView ? [{ label: 'Campo', key: 'farm' }] : []),
  { label: 'Sector', key: 'sector' },
  { label: 'Tipo Nodo', key: 'nodeType' },
  { label: 'Tipo de telemetría', key: 'telemetryType' },
  { label: 'Estado', key: 'status' },
  { label: 'Ver más', key: 'seeMore' },
];

const GetSensorListUI = (adminView: boolean) => [
  { label: 'Nombre', key: 'name' },
  ...(adminView ? [{ label: 'Cuenta', key: 'account' }] : []),
  ...(adminView ? [{ label: 'Campo', key: 'farm' }] : []),
  { label: 'Sector', key: 'sector' },
  { label: 'Nodo', key: 'nodeName' },
  { label: 'Tipo Sensor', key: 'sensorType' },
  { label: 'Tipo de telemetría', key: 'telemetryType' },
  { label: 'Estado', key: 'status' },
  { label: 'Ver más', key: 'seeMore' },
];

const stateTranslator = (status: string) => {
  switch (status) {
    case 'Operative':
      return 'Operativo';
    case 'Offline':
      return 'Fuera de línea';
    case 'Repair':
      return 'En reparación';
    case 'Disabled':
      return 'Deshabilitado';
    default:
      return status;
  }
};

const telemetryTypeTranslator = (telemetryType: string) => {
  switch (telemetryType) {
    case 'wiseconn':
      return 'Wiseconn';
    case 'sn':
      return 'THINGS4NUVA SL (ESPAÑA)';
    default:
      return 'Wiseconn';
  }
};

const oneButtonActionNode = (
  history: ReturnType<typeof useHistory>,
  id: string, farmId: string,
) => (
  <Box display='flex' alignItems='center' justifyContent='center'>
    <IconButton onClick={() => { history.push(`/layout/nodos/${id}?farmId=${farmId}`); }}>
      <ChevronRightIcon />
    </IconButton>
  </Box>
);

const oneButtonActionSensor = (history: ReturnType<typeof useHistory>,
  id: string, farmId: string) => (
  <Box display='flex' alignItems='center' justifyContent='center'>
    <IconButton onClick={() => { history.push(`/layout/sensorModels/${id}?farmId=${farmId}`); }}>
      <ChevronRightIcon />
    </IconButton>
  </Box>
);

interface DevicesTableContentProps {
  selectedAccountId?: string;
  selectedFarmId?: string;
  fixedFarmId?: string;
  adminView?: boolean;
  showFarmFilter?: boolean;
  fixedPaginationParameters?: {
    page: number;
    rowsPerPage: number;
  };
}

const DevicesTableContent: React.FC<DevicesTableContentProps> = ({
  selectedAccountId,
  selectedFarmId,
  adminView = true,
  fixedPaginationParameters,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      deviceType: (params.get('deviceType') as DeviceType) || 'nodes',
      viewMode: (params.get('viewMode') as 'table' | 'grid') || 'table',
      page: parseInt(params.get('page') || '0', 10),
      rowsPerPage: parseInt(params.get('rowsPerPage') || '10', 10),
      query: params.get('query') || '',
      selectedSensorType: params.get('sensorType') || '',
      selectedStatus: params.get('status') || '',
    };
  };

  const initialParams = getQueryParams();

  const [deviceType, setDeviceType] = useState<DeviceType>(initialParams.deviceType);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(initialParams.viewMode);
  const [paginationParameters, setPaginationParameters] = useState(
    fixedPaginationParameters || {
      page: initialParams.page,
      rowsPerPage: initialParams.rowsPerPage,
    },
  );
  const [query, setQuery] = useState(initialParams.query);
  const [loading, setLoading] = useState(false);
  const [nodesData, setNodesData] = useState<GetNodesPerPageResponse | null>(null);
  const [
    sensorModelsData,
    setSensorModelsData,
  ] = useState<GetSensorModelsPerPageResponse | null>(null);
  const [nodesSummary, setNodesSummary] = useState<SummaryState>({
    total: undefined,
    operative: undefined,
    offline: undefined,
    repair: undefined,
    disabled: undefined,
  });
  const [sensorModelsSummary, setSensorModelsSummary] = useState<{
    soil: SummaryState
    weather: SummaryState
    plant: SummaryState
    irrigation: SummaryState
    total: number
    operative: number
    offline: number
    repair: number
    disabled: number
  }>(
    {
      soil: {
        total: 0,
        operative: 0,
        offline: 0,
        repair: 0,
        disabled: 0,
      },
      weather: {
        total: 0,
        operative: 0,
        offline: 0,
        repair: 0,
        disabled: 0,
      },
      plant: {
        total: 0,
        operative: 0,
        offline: 0,
        repair: 0,
        disabled: 0,
      },
      irrigation: {
        total: 0,
        operative: 0,
        offline: 0,
        repair: 0,
        disabled: 0,
      },
      total: 0,
      operative: 0,
      offline: 0,
      repair: 0,
      disabled: 0,
    },
  );
  const [selectedSensorType, setSelectedSensorType] = useState<string>(
    initialParams.selectedSensorType,
  );

  const [selectedStatus, setSelectedStatus] = useState<string>(
    initialParams.selectedStatus || 'Offline',
  );

  const updateQueryParams = (updates: Partial<{
    deviceType: DeviceType;
    viewMode: 'table' | 'grid';
    page: number;
    rowsPerPage: number;
    query: string;
    selectedSensorType: string;
    selectedStatus: string;
  }>) => {
    const params = new URLSearchParams(location.search);

    if (updates.deviceType !== undefined) {
      params.set('deviceType', updates.deviceType);
    }
    if (updates.viewMode !== undefined) {
      params.set('viewMode', updates.viewMode);
    }
    if (updates.page !== undefined) {
      params.set('page', String(updates.page));
    }
    if (updates.rowsPerPage !== undefined) {
      params.set('rowsPerPage', String(updates.rowsPerPage));
    }
    if (updates.query !== undefined) {
      if (updates.query) {
        params.set('query', updates.query);
      } else {
        params.delete('query');
      }
    }
    if (updates.selectedSensorType !== undefined) {
      if (updates.selectedSensorType) {
        params.set('sensorType', updates.selectedSensorType);
      } else {
        params.delete('sensorType');
      }
    }
    if (updates.selectedStatus !== undefined) {
      if (updates.selectedStatus) {
        params.set('status', updates.selectedStatus);
      } else {
        params.delete('status');
      }
    }

    history.replace({ search: params.toString() });
  };

  const handleQuery = (valueToSearch: string) => {
    setQuery(valueToSearch);
    setPaginationParameters((prevState) => ({ ...prevState, page: 0 }));
    updateQueryParams({ query: valueToSearch, page: 0 });
  };

  const handlePaginationChange = (
    value: { page: number; rowsPerPage: number } |
    ((prev: { page: number; rowsPerPage: number }) => { page: number; rowsPerPage: number }),
  ) => {
    let newPagination: { page: number; rowsPerPage: number };
    if (typeof value === 'function') {
      newPagination = value(paginationParameters);
    } else {
      newPagination = value;
    }
    setPaginationParameters(newPagination);
    updateQueryParams({ page: newPagination.page, rowsPerPage: newPagination.rowsPerPage });
  };

  useEffect(() => {
    if (fixedPaginationParameters) {
      setPaginationParameters(fixedPaginationParameters);
    }
  }, [fixedPaginationParameters]);

  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const [nodesRes, sensorsRes] = await Promise.all([
          apiClient.customNodes.nodesSummary({
            accountId: selectedAccountId,
            farmId: selectedFarmId,
          } as any),
          apiClient.customSensorModels.sensorModelsSummary({
            accountId: selectedAccountId,
            farmId: selectedFarmId,
            sensorType: selectedSensorType,
          } as any),
        ]);
        if (nodesRes.data?.summary) setNodesSummary(nodesRes.data.summary);
        if (sensorsRes.data?.summary) setSensorModelsSummary(sensorsRes.data.summary);
      } catch (err) {
        console.error('Error fetching summaries', err);
      }
    };
    fetchSummaries();
  }, [selectedAccountId, selectedFarmId, selectedSensorType]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const commonParams = {
          page: viewMode === 'grid' ? 0 : paginationParameters.page,
          limit: viewMode === 'grid' ? 10000 : paginationParameters.rowsPerPage,
          searchValue: query,
          accountId: selectedAccountId,
          farmId: selectedFarmId,
          status: selectedStatus === 'All' ? '' : selectedStatus,
          sensorType: selectedSensorType,
          adminView: adminView ? 'true' : 'false',
        } as any;

        if (deviceType === 'nodes') {
          const response = await apiClient.customNodes.nodesList(commonParams);
          setNodesData(response.data as GetNodesPerPageResponse);
          setSensorModelsData(null);
        } else {
          const response = await apiClient.customSensorModels.sensorModelsList(commonParams);
          setSensorModelsData(response.data as GetSensorModelsPerPageResponse);
          setNodesData(null);
        }
      } catch (err) {
        const error = err as Error;
        enqueueSnackbar(error.message || 'Error al cargar datos', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [deviceType, paginationParameters.page,
    paginationParameters.rowsPerPage,
    query, selectedAccountId, selectedFarmId,
    selectedStatus, selectedSensorType, viewMode]);

  const mappedNodes = nodesData?.nodes?.map((elem) => ({
    // eslint-disable-next-line no-underscore-dangle
    id: elem._id?.toString() ?? uuidv4(),
    deviceType: 'Nodo',
    account: ((elem.farm as FarmSchema)?.accountId as AccountSchema)?.name ?? 'N/A',
    farm: (elem.farm as unknown as FarmSchema)?.name ?? 'N/A',
    name: elem.name ?? 'N/A',
    nodeType: elem.nodeType ?? 'N/A',
    modemId: elem.modemId ?? 'N/A',
    telemetryType: telemetryTypeTranslator(elem.telemetryType || 'wiseconn'),
    sensorType: 'N/A',
    status: (
      <>
        <Typography variant="body2" sx={{ color: STATUS_COLORS[elem.status as keyof typeof STATUS_COLORS] || 'inherit' }}>
          {stateTranslator(elem.status)}
        </Typography>
        {elem.lastActivity && elem.status === 'Offline' && (
          <>
            <Typography variant="body2" sx={{ color: STATUS_COLORS[elem.status as keyof typeof STATUS_COLORS] || 'inherit' }}>
              ({dayjs().diff(dayjs(elem.lastActivity), 'days')} días)
            </Typography>
          </>
        )}
      </>),
    rawStatus: elem.status,
    sector: elem?.sector?.name ?? 'N/A',
    nodeName: 'N/A',
    unit: 'N/A',
    sensorsList: 'N/A',
    // eslint-disable-next-line no-underscore-dangle
    seeMore: oneButtonActionNode(history, elem._id?.toString() ?? '', elem.farm?._id?.toString() ?? ''),
  })) ?? [];

  const mappedSensors = sensorModelsData?.sensorModels?.map((elem) => ({
    // eslint-disable-next-line no-underscore-dangle
    id: elem._id?.toString() ?? uuidv4(),
    deviceType: capitalizeFirstLetter(elem?.type ?? ''),
    account: elem.account?.name ?? 'N/A',
    farm: elem?.farm?.name ?? 'N/A',
    name: elem?.name || (elem as SensorModelSchema)?.sensors?.[0]?.name || 'N/A',
    telemetryType: telemetryTypeTranslator(elem.telemetryType || 'wiseconn'),
    sector: elem?.sector?.name ?? 'N/A',
    status: (
      <>
        <Typography variant="body2" sx={{ color: STATUS_COLORS[elem.status as keyof typeof STATUS_COLORS] || 'inherit' }}>
          {stateTranslator(elem.status)}
        </Typography>
        {elem.lastActivity && elem.status === 'Offline' && (
          <>
            <Typography variant="body2" sx={{ color: STATUS_COLORS[elem.status as keyof typeof STATUS_COLORS] || 'inherit' }}>
              ({dayjs().diff(dayjs(elem.lastActivity), 'days')} días)
            </Typography>
          </>
        )}
      </>),
    rawStatus: elem.status,
    nodeName: elem.node?.name ?? 'N/A',
    sensorType: elem.sensorType,
    // eslint-disable-next-line no-underscore-dangle
    seeMore: oneButtonActionSensor(history, elem.id?.toString() ?? '', elem.farm?._id?.toString() ?? ''),
  })) ?? [];

  let localData;
  let headers;

  if (deviceType === 'nodes') {
    localData = {
      count: nodesData?.totalNodes ?? 0,
      values: mappedNodes,
    };
    headers = GetNodesListUI(adminView);
  } else {
    localData = {
      count: sensorModelsData?.totalSensorModels ?? 0,
      values: mappedSensors,
    };
    headers = GetSensorListUI(adminView);
  }

  const StatCard = ({
    title, total, operative, offline,
    disabled, backgroundColor, sx,
  }: {
    title: string;
    total: number | undefined;
    operative: number | undefined;
    offline: number | undefined;
    disabled: number | undefined;
    backgroundColor?: string;
    sx?: SxProps;
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: '1px solid #e0e0e0',
        flex: 1,
        ...(adminView && { height: '180px' }),
        ...(!adminView && { height: '140px' }),
        width: '200px',
        backgroundColor,
        marginBottom: 1,
        ...sx,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ color: '#385576', fontWeight: 530 }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#385576' }}>
          {total ?? 0}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#4caf50',
            }}
          />
          <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
            Operativos
          </Typography>
          <Typography variant="body2" sx={{ color: '#424242', fontWeight: 600, ml: 'auto' }}>
            {operative ?? 0}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#f44336',
            }}
          />
          <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
            Fuera de línea
          </Typography>
          <Typography variant="body2" sx={{ color: '#424242', fontWeight: 600, ml: 'auto' }}>
            {offline ?? 0}
          </Typography>
        </Box>
        {adminView && <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#9e9e9e',
            }}
          />
          <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
            Deshabilitado
          </Typography>
          <Typography variant="body2" sx={{ color: '#424242', fontWeight: 600, ml: 'auto' }}>
            {disabled ?? 0}
          </Typography>
        </Box>}
      </Box>
    </Paper>
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Operative':
        return '#4caf50';
      case 'Offline':
        return '#f44336';
      case 'Repair':
        return '#ff9800';
      case 'Disabled':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const DeviceCard = ({ device, type }: {
    device: {
      id: string;
      name: string;
      account?: string;
      farm?: string;
      sector: string;
      nodeType?: string;
      modemId?: string;
      nodeName?: string;
      sensorType?: string;
      telemetryType?: string;
      status: React.ReactNode;
      lastActivity?: string;
    };
    type: 'node' | 'sensor';
  }) => {
    const deviceData = type === 'node'
      // eslint-disable-next-line no-underscore-dangle
      ? (nodesData?.nodes?.find((n) => n._id?.toString() === device.id?.toString()))
      // eslint-disable-next-line no-underscore-dangle
      : (sensorModelsData?.sensorModels?.find((s) => s._id?.toString() === device.id?.toString()));
    const rawStatus = deviceData?.status || 'Offline';
    return (
      <Card
        sx={{
          height: '95%',
          borderLeft: `12px solid ${getStatusColor(rawStatus)}`,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 3,
          },
          backgroundColor: '#AAAAAA1A',
          width: '100%',
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              {device.name}
            </Typography>
          </Box>
          <Divider />

          <Box display="flex" flexDirection="row" gap={1} mt={1} justifyContent="space-between">
            <Box display="flex" flexDirection="column" gap={1}>

              <Box display="flex" flexDirection="row" gap={1}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 400 }}
                  variant="caption" color="textSecondary">
                  Sector
                </Typography>
                <Typography
                  color="#385576"
                  fontWeight={550}
                  variant="body2">{device.sector}</Typography>
              </Box>

              {type === 'node' ? (
                <>
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Typography
                      sx={{ fontSize: '0.875rem', fontWeight: 400 }}
                      variant="caption"
                      color="textSecondary">
                      Modelo
                    </Typography>
                    <Typography
                      color="#385576"
                      fontWeight={550}
                      variant="body2">{device.nodeType}</Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Typography
                      sx={{ fontSize: '0.875rem', fontWeight: 400 }}
                      variant="caption" color="textSecondary">
                      Nodo
                    </Typography>
                    <Typography
                      color="#385576"
                      fontWeight={550}
                      variant="body2">{device.nodeName}</Typography>
                  </Box>
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Typography
                      sx={{ fontSize: '0.875rem', fontWeight: 400 }}
                      variant="caption" color="textSecondary">
                      Tipo Sensor
                    </Typography>
                    <Typography
                      color="#385576"
                      fontWeight={550}
                      variant="body2">{device.sensorType}</Typography>
                  </Box>
                </>
              )}

              <Box display="flex" flexDirection="row" gap={1}>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 400 }}
                  variant="caption" color="textSecondary">
                  Telemetría
                </Typography>
                <Typography
                  color="#385576"
                  fontWeight={550}
                  variant="body2">{telemetryTypeTranslator(device?.telemetryType || 'wiseconn')}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="flex-end">
              <Box >{device.status}</Box>
              <ChevronRightIcon
                onClick={() => {
                  if (type === 'node') {
                    const nodeData = nodesData?.nodes?.find((n) => n.id === device.id);
                    // eslint-disable-next-line no-underscore-dangle
                    history.push(`/layout/nodos/${device.id?.toString()}?farmId=${nodeData?.farm?._id}`);
                  } else {
                    const sensorData = sensorModelsData?.sensorModels?.find(
                      // eslint-disable-next-line no-underscore-dangle
                      (s) => s._id?.toString() === device.id?.toString(),
                    );
                    // eslint-disable-next-line no-underscore-dangle
                    history.push(`/layout/sensorModels/${device.id}?farmId=${sensorData?.farm?._id}`);
                  }
                }}
                sx={{ ml: 1, cursor: 'pointer' }}
              >
              </ChevronRightIcon>

            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Box display="flex" gap={0.5}>
        <Box>
          <StatCard
            title="Nodos totales"
            total={nodesSummary.total}
            operative={nodesSummary.operative}
            offline={nodesSummary.offline}
            disabled={nodesSummary.disabled}
            sx={{ borderRadius: 2 }}
          />
        </Box>
        <Box display="flex" gap={0} mb={2} sx={{ overflowX: 'auto' }}>
          <StatCard
            title="Sensores totales"
            total={sensorModelsSummary.total}
            operative={sensorModelsSummary.operative}
            offline={sensorModelsSummary.offline}
            disabled={sensorModelsSummary.disabled}
            sx={{
              borderRight: '2px solid #385576',
              borderTopLeftRadius: 4,
              borderBottomLeftRadius: 4,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          />
          <StatCard
            title="Sensores de suelo"
            total={sensorModelsSummary.soil.total}
            operative={sensorModelsSummary.soil.operative}
            offline={sensorModelsSummary.soil.offline}
            disabled={sensorModelsSummary.soil.disabled}
            backgroundColor='#F9F9F9'
            sx={{ borderRadius: 0 }}
          />
          <StatCard
            title="Sensores de planta"
            total={sensorModelsSummary.plant.total}
            operative={sensorModelsSummary.plant.operative}
            offline={sensorModelsSummary.plant.offline}
            disabled={sensorModelsSummary.plant.disabled}
            backgroundColor='#F9F9F9'
            sx={{ borderRadius: 0 }}
          />
          <StatCard
            title="Sensores de clima"
            total={sensorModelsSummary.weather.total}
            operative={sensorModelsSummary.weather.operative}
            offline={sensorModelsSummary.weather.offline}
            disabled={sensorModelsSummary.weather.disabled}
            backgroundColor='#F9F9F9'
            sx={{ borderRadius: 0 }}
          />
          <StatCard
            title="Sensores de riego"
            total={sensorModelsSummary.irrigation.total}
            operative={sensorModelsSummary.irrigation.operative}
            offline={sensorModelsSummary.irrigation.offline}
            disabled={sensorModelsSummary.irrigation.disabled}
            backgroundColor='#F9F9F9'
            sx={{
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          />
        </Box>
      </Box>

      <Box>
        <Card>
          <Tabs
            value={deviceType}
            onChange={(e, newValue) => {
              const newSensorType = newValue === 'nodes' ? '' : selectedSensorType;
              setSelectedSensorType(newSensorType);
              setDeviceType(newValue);
              setPaginationParameters({ page: 0, rowsPerPage: 10 });
              setQuery('');
              updateQueryParams({
                deviceType: newValue,
                selectedSensorType: newSensorType,
                page: 0,
                rowsPerPage: 10,
                query: '',
              });
            }}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTabs-indicator': {
                height: 3,
              },
            }}
          >
            <Tab
              label="Nodos"
              value="nodes"
              sx={{
                flex: 1,
                maxWidth: 'none',
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
            <Tab
              label="Sensores"
              value="sensors"
              sx={{
                flex: 1,
                maxWidth: 'none',
                fontSize: '1rem',
                fontWeight: 500,
              }}
            />
          </Tabs>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} p={2}>
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              <CustomSearch query={query} handleQuery={handleQuery} />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="sensor-type-label">Tipo de sensor</InputLabel>
                <Select
                  labelId="sensor-type-label"
                  value={selectedSensorType}
                  label="Tipo de sensor"
                  onChange={(e) => {
                    setSelectedSensorType(e.target.value);
                    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
                    updateQueryParams({ selectedSensorType: e.target.value, page: 0 });
                  }}
                  disabled={deviceType === 'nodes'}
                >
                  <MenuItem value="">Sin filtro</MenuItem>
                  <MenuItem value="Suelo">Suelo</MenuItem>
                  <MenuItem value="Clima">Clima</MenuItem>
                  <MenuItem value="Planta">Planta</MenuItem>
                  <MenuItem value="Riego">Riego</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label">Estado</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={selectedStatus}
                  label="Estado"
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
                    updateQueryParams({ selectedStatus: e.target.value, page: 0 });
                  }}
                >
                  <MenuItem value="All">Todos los estados</MenuItem>
                  <MenuItem value="Operative">Operativo</MenuItem>
                  <MenuItem value="Repair">En reparación</MenuItem>
                  <MenuItem value="Offline">Fuera de línea</MenuItem>
                  {adminView && <MenuItem value="Disabled">Deshabilitado</MenuItem>}
                </Select>
              </FormControl>
            </Box>
            {!adminView && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                    setPaginationParameters((prev) => ({ ...prev, page: 0 }));
                    updateQueryParams({ viewMode: newMode, page: 0 });
                  }
                }}
                size="small"
              >
                <ToggleButton value="table" aria-label="vista tabla">
                  <ViewList />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="vista grilla">
                  <ViewModule />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size="3rem" />
            </Box>
          )}
          {!loading && viewMode === 'table' && (
            <CustomTable
              rows={{ values: localData.values, count: localData.count }}
              headers={headers}
              paginationParameters={paginationParameters}
              setPaginationParameters={handlePaginationChange}
            />
          )}
          {!loading && viewMode === 'grid' && (
            <Box p={2}>
              <Grid container spacing={2}>
                {localData.values.map((device) => (
                  // eslint-disable-next-line react/prop-types
                  <Grid item xs={12} sm={6} md={4} key={device.id}>
                    <DeviceCard
                      device={device}
                      type={deviceType === 'nodes' ? 'node' : 'sensor'}
                    />
                  </Grid>
                ))}
              </Grid>
              {localData.values.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron resultados
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default DevicesTableContent;
